import { useEffect, useMemo } from "react";
import { Outlet, useLocation, matchPath } from "react-router";
import { isOriginalSlug } from "@laftel-ip-originals/notion/originals";
import { TopButton } from "./top-button";
import { Cursor } from "./cursor";
import { Loading } from "./loading";
import { HomeLoading } from "./home-loading";
import { resolveIpAssets, resolveSiteTheme } from "~/theme/resolve-theme";
import { useIpTheme } from "~/theme/use-ip-theme";
import { useI18n } from "~/i18n";
import { ORIGINALS_LIST } from "~/data/ip";

export function Layout() {
  const { pathname, hash } = useLocation();
  const { locale } = useI18n();
  const match = matchPath({ path: "/:slug/*", end: false }, pathname);
  const slug = match?.params.slug;
  const theme = resolveSiteTheme(slug);
  useIpTheme(theme);

  // 라우트 변경 시 스크롤 최상단 초기화. 해시 내비는 useLenis 가 앵커 스크롤로 처리.
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);

  const isIpRoot = Boolean(slug && isOriginalSlug(slug) && pathname === `/${slug}`);
  const isHome = pathname === "/";

  // locale 변경 시에도 새 PV 미리보기 영상이 버퍼링될 시간을 주기 위해 Loading 을 재실행.
  const ipLoading = useMemo(() => {
    if (!isIpRoot || !slug) return null;
    const assets = resolveIpAssets(slug);
    const pvLocale = locale === "zh-cn" || locale === "zh-tw" ? "en" : locale;
    const pvSrc =
      assets.pvPreviewByLocale?.[pvLocale] ?? assets.pvPreviewByLocale?.en ?? assets.pvPreview;
    return {
      ...assets.loading,
      videoAssets: pvSrc ? [pvSrc] : [],
    };
  }, [isIpRoot, slug, locale]);

  const homeLoadingAssets = useMemo(
    () => (isHome ? ORIGINALS_LIST.map((o) => o.thumbnail) : null),
    [isHome],
  );

  const hideTopButton = pathname === "/" || /\/goods\/[^/]+$/.test(pathname);

  return (
    <div className="min-h-dvh bg-[var(--ip-bg)] text-[var(--ip-text)]">
      {ipLoading ? <Loading {...ipLoading} key={`${slug}-${locale}`} /> : null}
      {homeLoadingAssets ? <HomeLoading assets={homeLoadingAssets} key={`home-${locale}`} /> : null}
      <main className="relative z-[1]">
        <Outlet />
      </main>
      {!hideTopButton && <TopButton />}
      {pathname !== "/" && <Cursor />}
    </div>
  );
}
